/**
 * Brands Hook
 * 
 * Fetches all available brands for filtering products.
 * 
 * Usage:
 * ```typescript
 * const { brands, isLoading } = useBrands();
 * 
 * // Pass to filter panel
 * <ProductFilterPanel availableBrands={brands.map(b => b.id.toString())} />
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabaseClient';
import { PRODUCT_CACHE_TIME } from './queryKeys';

export interface Brand {
  id: number;
  name: string;
  description?: string;
  image_url?: string;
  is_hidden?: boolean;
  created_at?: string;
}

export interface UseBrandsResult {
  brands: Brand[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetch all available brands (not hidden)
 * 
 * @returns Brands array with loading/error states
 */
export function useBrands(): UseBrandsResult {
  const cacheKey = ['brands', 'all'] as const;

  const brandsQuery = useQuery({
    queryKey: cacheKey,
    queryFn: async () => {
      // Call the brands RPC
      const { data, error } = await supabase.rpc('get_available_brands');

      if (error) {
        console.error('[useBrands] Error:', error);
        throw new Error(`Failed to fetch brands: ${error.message}`);
      }

      const brands = ((data as any)?.brands as Brand[]) || [];
      return brands;
    },
    staleTime: PRODUCT_CACHE_TIME.filters, // 30 min - brands don't change often
    gcTime: PRODUCT_CACHE_TIME.filters * 2, // Keep in memory for 60 min
    retry: 2,
  });

  const result = {
    brands: brandsQuery.data || [],
    isLoading: brandsQuery.isLoading,
    isError: brandsQuery.isError,
    error: brandsQuery.error as Error | null,
    refetch: brandsQuery.refetch as () => void,
  };

  return result;
}
